import React, { useState, useEffect } from 'react';
import { useTheme } from '../ThemeContext';
import { EarningsService, EarningsCalendarItem } from '../../services/EarningsService';
import { EarningsCard } from './EarningsCard';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Loader, Filter } from 'lucide-react';

type ViewMode = 'month' | 'week';
type CardViewType = 'simple' | 'advanced';

const SECTOR_OPTIONS = [
    'All',
    'Technology',
    'Healthcare',
    'Financial Services',
    'Consumer Cyclical',
    'Industrials',
    'Communication Services',
    'Consumer Defensive',
    'Energy',
    'Basic Materials',
    'Real Estate',
    'Utilities'
];

const MARKET_CAP_OPTIONS = [
    { label: 'All', min: 0, max: Infinity },
    { label: 'Mega Cap (>$200B)', min: 200e9, max: Infinity },
    { label: 'Large Cap ($10B - $200B)', min: 10e9, max: 200e9 },
    { label: 'Mid Cap ($2B - $10B)', min: 2e9, max: 10e9 },
    { label: 'Small Cap (<$2B)', min: 0, max: 2e9 },
];

export const EarningsCalendarView: React.FC = () => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const [earnings, setEarnings] = useState<EarningsCalendarItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [viewMode] = useState<ViewMode>('week');
    const [selectedSector, setSelectedSector] = useState('All');
    const [selectedMarketCap, setSelectedMarketCap] = useState(0);
    const [showFilters, setShowFilters] = useState(true);
    const [cardViewType] = useState<CardViewType>('advanced');

    const formatDate = (date: Date) => date.toISOString().split('T')[0];

    const getMonthRange = (date: Date) => {
        const start = new Date(date.getFullYear(), date.getMonth(), 1);
        const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        return { start, end };
    };

    const getWeekRange = (date: Date) => {
        const start = new Date(date);
        const day = start.getDay();
        start.setDate(start.getDate() - day);
        start.setHours(0, 0, 0, 0);

        const end = new Date(start);
        end.setDate(end.getDate() + 6);
        end.setHours(23, 59, 59, 999);

        return { start, end };
    };

    const fetchEarnings = async () => {
        setLoading(true);
        const { start, end } = viewMode === 'month' ? getMonthRange(currentDate) : getWeekRange(currentDate);

        try {
            const data = await EarningsService.getEarningsCalendar(formatDate(start), formatDate(end));
            setEarnings(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEarnings();
    }, [currentDate, viewMode]);

    const handlePrev = () => {
        const newDate = new Date(currentDate);
        if (viewMode === 'month') {
            newDate.setMonth(newDate.getMonth() - 1);
            newDate.setDate(1);
        } else {
            newDate.setDate(newDate.getDate() - 7);
        }
        setCurrentDate(newDate);
    };

    const handleNext = () => {
        const newDate = new Date(currentDate);
        if (viewMode === 'month') {
            newDate.setMonth(newDate.getMonth() + 1);
            newDate.setDate(1);
        } else {
            newDate.setDate(newDate.getDate() + 7);
        }
        setCurrentDate(newDate);
    };

    const handleToday = () => {
        setCurrentDate(new Date());
    };

    const getDaysInWeek = () => {
        const { start } = getWeekRange(currentDate);
        const days = [];
        for (let i = 0; i < 7; i++) {
            const day = new Date(start);
            day.setDate(start.getDate() + i);
            if (day.getDay() >= 1 && day.getDay() <= 5) {
                days.push(day);
            }
        }
        return days;
    };

    const filteredEarnings = earnings.filter(item => {
        const marketCapFilter = MARKET_CAP_OPTIONS[selectedMarketCap];
        const passesMarketCap = item.marketCap >= marketCapFilter.min && item.marketCap < marketCapFilter.max;
        const passesSector = selectedSector === 'All' || item.sector === selectedSector;
        return passesMarketCap && passesSector;
    });

    const groupedEarnings = filteredEarnings.reduce((acc, item) => {
        if (!acc[item.date]) acc[item.date] = [];
        acc[item.date].push(item);
        return acc;
    }, {} as Record<string, EarningsCalendarItem[]>);

    const getHeaderTitle = () => {
        const { start, end } = getWeekRange(currentDate);
        const startStr = start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
        const endStr = end.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
        if (start.getFullYear() !== end.getFullYear()) {
            return `Week of ${startStr}, ${start.getFullYear()} - ${endStr}, ${end.getFullYear()}`;
        }
        return `Week of ${startStr} - ${endStr}, ${start.getFullYear()}`;
    };

    const daysInWeek = getDaysInWeek();

    return (
        <div className="w-full">
            <div className="flex flex-col gap-3 mb-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {getHeaderTitle()}
                        </h2>
                        <div className="flex gap-1">
                            <button
                                onClick={handlePrev}
                                className={`p-2 rounded-full hover:bg-opacity-20 ${isDark ? 'hover:bg-gray-400 text-gray-300' : 'hover:bg-gray-200 text-gray-600'}`}
                            >
                                <ChevronLeft size={20} />
                            </button>
                            <button
                                onClick={handleNext}
                                className={`p-2 rounded-full hover:bg-opacity-20 ${isDark ? 'hover:bg-gray-400 text-gray-300' : 'hover:bg-gray-200 text-gray-600'}`}
                            >
                                <ChevronRight size={20} />
                            </button>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* View Toggle */}
                        {/* View Toggle - Commented out as per request to enforce Advanced view */}
                        {/* <div className={`flex p-1 rounded-lg border ${isDark ? 'border-theme-tertiary bg-theme-primary' : 'border-gray-200 bg-gray-50'}`}>
                            <button
                                onClick={() => setCardViewType('simple')}
                                className={`px-3 py-1 text-xs font-medium rounded-md transition-all flex items-center gap-1.5 ${cardViewType === 'simple'
                                    ? (isDark ? 'bg-theme-secondary text-white shadow-sm' : 'bg-white text-gray-900 shadow-sm')
                                    : (isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700')
                                    }`}
                            >
                                <LayoutGrid size={12} />
                                Simple
                            </button>
                            <button
                                onClick={() => setCardViewType('advanced')}
                                className={`px-3 py-1 text-xs font-medium rounded-md transition-all flex items-center gap-1.5 ${cardViewType === 'advanced'
                                    ? (isDark ? 'bg-theme-secondary text-white shadow-sm' : 'bg-white text-gray-900 shadow-sm')
                                    : (isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700')
                                    }`}
                            >
                                <List size={12} />
                                Advanced
                            </button>
                        </div> */}

                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`px-3 py-1.5 text-sm rounded-lg border flex items-center gap-2 transition-colors ${showFilters
                                ? isDark ? 'border-blue-500 text-blue-400 bg-blue-500/10' : 'border-blue-500 text-blue-600 bg-blue-50'
                                : isDark ? 'border-theme-tertiary text-theme-muted hover:text-white hover:border-theme-accent' : 'border-gray-300 text-gray-600 hover:text-gray-900 hover:border-blue-400'
                                }`}
                        >
                            <Filter size={14} />
                            Filters
                        </button>
                        <button
                            onClick={handleToday}
                            className={`px-3 py-1.5 text-sm rounded-lg border flex items-center gap-2 transition-colors ${isDark
                                ? 'border-theme-tertiary text-theme-muted hover:text-white hover:border-theme-accent'
                                : 'border-gray-300 text-gray-600 hover:text-gray-900 hover:border-blue-400'
                                }`}
                        >
                            <CalendarIcon size={14} />
                            Today
                        </button>
                    </div>
                </div>

                {showFilters && (
                    <div className={`px-4 py-2 rounded-lg border flex items-center gap-4 flex-wrap ${isDark ? 'bg-theme-secondary border-theme-tertiary' : 'bg-gray-50 border-gray-200'}`}>
                        <div className="flex items-center gap-2">
                            <label className={`text-xs font-medium whitespace-nowrap ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                Sector:
                            </label>
                            <select
                                value={selectedSector}
                                onChange={(e) => setSelectedSector(e.target.value)}
                                className={`px-2.5 py-1 rounded-md border text-xs ${isDark
                                    ? 'bg-theme-primary border-theme-tertiary text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                                    }`}
                            >
                                {SECTOR_OPTIONS.map(sector => (
                                    <option key={sector} value={sector}>{sector}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex items-center gap-2">
                            <label className={`text-xs font-medium whitespace-nowrap ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                Market Cap:
                            </label>
                            <select
                                value={selectedMarketCap}
                                onChange={(e) => setSelectedMarketCap(Number(e.target.value))}
                                className={`px-2.5 py-1 rounded-md border text-xs ${isDark
                                    ? 'bg-theme-primary border-theme-tertiary text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                                    }`}
                            >
                                {MARKET_CAP_OPTIONS.map((option, idx) => (
                                    <option key={idx} value={idx}>{option.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className={`text-xs ml-auto ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {filteredEarnings.length} of {earnings.length} earnings
                        </div>
                    </div>
                )}
            </div>

            {loading ? (
                <div className="flex justify-center items-center h-64">
                    <Loader className="animate-spin text-blue-500" size={40} />
                </div>
            ) : (
                <div className={`rounded-lg border overflow-hidden ${isDark ? 'border-theme-tertiary bg-theme-secondary' : 'border-gray-200 bg-white'}`}>
                    <div className="overflow-x-auto">
                        <div className="min-w-[1000px]">
                            <div className="grid grid-cols-5 gap-px bg-gray-200 dark:bg-gray-700">
                                {daysInWeek.map((day, idx) => {
                                    const isToday = formatDate(day) === formatDate(new Date());
                                    return (
                                        <div
                                            key={idx}
                                            className={`p-3 text-center ${isDark ? 'bg-theme-secondary' : 'bg-gray-50'}`}
                                        >
                                            <div className={`text-xs font-semibold mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                                {day.toLocaleDateString(undefined, { weekday: 'short' }).toUpperCase()}
                                            </div>
                                            <div className={`text-sm font-bold ${isToday
                                                ? 'text-blue-600 dark:text-blue-400'
                                                : isDark ? 'text-white' : 'text-gray-900'
                                                }`}>
                                                {day.getDate()}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="grid grid-cols-5 gap-px bg-gray-200 dark:bg-gray-700 min-h-[400px]">
                                {daysInWeek.map((day, idx) => {
                                    const dateStr = formatDate(day);
                                    const dayEarnings = groupedEarnings[dateStr] || [];
                                    const isToday = dateStr === formatDate(new Date());

                                    return (
                                        <div
                                            key={idx}
                                            className={`p-2 overflow-y-auto ${isDark ? 'bg-theme-primary' : 'bg-white'
                                                } ${isToday ? (isDark ? 'bg-blue-900/20' : 'bg-blue-50/50') : ''}`}
                                        >
                                            <div className={cardViewType === 'simple' ? 'grid grid-cols-2 gap-2' : 'space-y-2'}>
                                                {dayEarnings.map((item, earningIdx) => (
                                                    <EarningsCard
                                                        key={`${item.symbol}-${earningIdx}`}
                                                        item={item}
                                                        viewType={cardViewType}
                                                    />
                                                ))}
                                            </div>
                                            {dayEarnings.length === 0 && (
                                                <div className={`text-center text-xs py-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                                                    No earnings
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
